import { useEffect, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { ProjectScheduleRow } from '../../lib/types';
import type { Density } from './ScheduleFilters';

interface ProjectScheduleGridProps {
  rows: ProjectScheduleRow[];
  days: string[];
  density: Density;
  onProjectClick: (jobNumber: string) => void;
}

const PROJECT_COL_WIDTH = 240;
const MIN_DAY_COL_WIDTH = 60;
const CHIP_HEIGHT = 22;          // height of one crew pill
const CHIP_GAP = 3;
const ROW_PADDING = 8;
const MIN_ROW_HEIGHT = CHIP_HEIGHT + ROW_PADDING;

function rowHeightForMaxItems(maxItems: number): number {
  if (maxItems <= 1) return MIN_ROW_HEIGHT;
  return maxItems * CHIP_HEIGHT + (maxItems - 1) * CHIP_GAP + ROW_PADDING;
}

function formatDayHeader(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00');
  return {
    month: d.toLocaleString('default', { month: 'short' }),
    day: d.getDate(),
    weekday: d.toLocaleString('default', { weekday: 'short' }),
  };
}

function isWeekend(dateStr: string): boolean {
  const d = new Date(dateStr + 'T00:00:00');
  const dow = d.getDay();
  return dow === 0 || dow === 6;
}

function isToday(dateStr: string): boolean {
  return dateStr === new Date().toISOString().slice(0, 10);
}

function dayColWidthFor(density: Density, availableWidth: number): number {
  if (availableWidth <= 0) return MIN_DAY_COL_WIDTH;
  if (density === 'day') return Math.max(availableWidth, MIN_DAY_COL_WIDTH);
  return Math.max(availableWidth / 7, MIN_DAY_COL_WIDTH);
}

export function ProjectScheduleGrid({ rows, days, density, onProjectClick }: ProjectScheduleGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerWidth(el.clientWidth);
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? 0;
      setContainerWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const rowHeights = rows.map((row) => {
    const maxItems = row.cells.reduce((m, c) => Math.max(m, c.items.length), 0);
    return rowHeightForMaxItems(maxItems);
  });

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: (index) => rowHeights[index] ?? MIN_ROW_HEIGHT,
    overscan: 5,
  });

  const availableWidth = Math.max(0, containerWidth - PROJECT_COL_WIDTH);
  const dayColWidth = dayColWidthFor(density, availableWidth);
  const totalWidth = PROJECT_COL_WIDTH + days.length * dayColWidth;

  return (
    <div
      ref={containerRef}
      className="overflow-auto flex-1 min-h-0 bg-white"
    >
      <div style={{ width: totalWidth, position: 'relative' }}>
        {/* Sticky date header */}
        <div
          className="sticky top-0 z-10 bg-white border-b border-slate-400 flex"
          style={{ width: totalWidth }}
        >
          <div
            className="sticky left-0 z-20 bg-white border-r border-slate-400 flex items-center px-2"
            style={{ width: PROJECT_COL_WIDTH, minWidth: PROJECT_COL_WIDTH }}
          >
            <span className="text-xs font-semibold text-brand-charcoal/70">Project</span>
          </div>
          {days.map((d) => {
            const { month, day, weekday } = formatDayHeader(d);
            const today = isToday(d);
            return (
              <div
                key={d}
                role="columnheader"
                className={`text-center border-r border-slate-300 shrink-0 ${today ? 'border-b-2 border-b-brand-orange' : ''}`}
                style={{ width: dayColWidth, minWidth: dayColWidth }}
              >
                <div className="text-[10px] text-slate-400 leading-none pt-1">{weekday} · {month}</div>
                <div className={`text-xs leading-none pb-1 ${today ? 'font-bold text-brand-orange' : 'text-slate-600'}`}>
                  {day}
                </div>
              </div>
            );
          })}
        </div>

        {/* Virtualized project rows */}
        <div
          role="grid"
          aria-label="Project schedule grid"
          style={{ height: rowVirtualizer.getTotalSize(), position: 'relative' }}
        >
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index];
            const rowHeight = rowHeights[virtualRow.index];
            return (
              <div
                key={row.projectId}
                className="absolute flex border-b border-slate-300"
                style={{ top: virtualRow.start, height: rowHeight, width: totalWidth }}
              >
                {/* Project header cell */}
                <button
                  type="button"
                  role="rowheader"
                  onClick={() => onProjectClick(row.jobNumber)}
                  title={`${row.jobNumber} — ${row.projectName}`}
                  className="sticky left-0 z-10 bg-white border-r border-slate-400 flex items-center gap-2 px-2 shrink-0 text-left hover:bg-slate-50"
                  style={{ width: PROJECT_COL_WIDTH, minWidth: PROJECT_COL_WIDTH }}
                >
                  <span
                    className="w-2.5 h-2.5 rounded-sm shrink-0"
                    style={{ backgroundColor: row.colorHex }}
                    aria-hidden="true"
                  />
                  <span className="flex flex-col min-w-0">
                    <span className="text-sm font-medium text-brand-blue truncate">{row.projectName}</span>
                    <span className="text-xs text-brand-charcoal/60 truncate">
                      {row.jobNumber}
                      {row.serviceLine ? ` · ${row.serviceLine}` : ''}
                      {row.account ? ` · ${row.account}` : ''}
                    </span>
                  </span>
                </button>

                {/* Day cells — crew pills */}
                {days.map((d) => {
                  const cell = row.cells.find((c) => c.date === d);
                  const weekend = isWeekend(d);
                  const items = cell?.items ?? [];
                  return (
                    <div
                      key={d}
                      className={`border-r border-slate-300 shrink-0 flex flex-col px-1 py-1 justify-start ${weekend ? 'bg-slate-50' : ''}`}
                      style={{ width: dayColWidth, minWidth: dayColWidth, gap: CHIP_GAP }}
                    >
                      {items.length === 0 ? (
                        <span className="text-slate-300 text-center text-xs select-none w-full block self-center">—</span>
                      ) : (
                        items.map((item) => (
                          <span
                            key={item.assignmentId}
                            title={`${item.displayName}${item.region ? ` (${item.region})` : ''}`}
                            className="truncate rounded px-1.5 text-xs leading-[18px] h-[22px] flex items-center bg-brand-blue/10 text-brand-charcoal"
                          >
                            {item.displayName}
                          </span>
                        ))
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
